import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  Brain, 
  TrendingUp, 
  TrendingDown, 
  CheckCircle2, 
  XCircle, 
  Edit2, 
  Plus,
  ArrowRight,
  Clock,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  RefreshCw,
  Settings,
  Loader2,
  BarChart3,
  ListChecks,
  Trash2,
  UserCheck,
  Zap,
  Lightbulb,
  Info,
  PlayCircle,
  Eye,
  GitCompare
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';

const BrainLearningPanel = () => {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [activeTab, setActiveTab] = useState('resumen');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [patronesPrecios, setPatronesPrecios] = useState(null);
  const [patronesPlazos, setPatronesPlazos] = useState(null);
  // Fases 6-9: Nuevos estados
  const [resumenAvanzado, setResumenAvanzado] = useState(null);
  const [patronesRelevancia, setPatronesRelevancia] = useState(null);
  const [patronesReasignacion, setPatronesReasignacion] = useState(null);
  const [metricasTiempo, setMetricasTiempo] = useState(null);
  const [especializacionFuncionarios, setEspecializacionFuncionarios] = useState(null);
  // Fases 10-13: Motor de recomendación
  const [motorEstado, setMotorEstado] = useState(null);
  // Fase 10 (PSP): patrones aprendidos de diferencias PSP sugerida vs final
  const [pspPatrones, setPspPatrones] = useState(null);
  // Fase 10 (PSP): feedback del Líder del Área Comercial
  const [pspLiderFb, setPspLiderFb] = useState(null);
  const [testTarea, setTestTarea] = useState({
    titulo: '',
    descripcion: '',
    area: 'Tributaria',
    plazo_dias: 7,
    precio: null
  });
  const [testResultado, setTestResultado] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  // SHADOW Mode
  const [shadowStats, setShadowStats] = useState(null);
  const [shadowLista, setShadowLista] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, configRes, feedbackRes, sugerenciasRes, preciosRes, plazosRes] = await Promise.all([
        api.get('/cerebro/aprendizaje/stats'),
        api.get('/cerebro/aprendizaje/config'),
        api.get('/cerebro/aprendizaje/feedback?limit=20'),
        api.get('/cerebro/aprendizaje/sugerencias?limit=10'),
        api.get('/cerebro/aprendizaje/patrones-precio'),
        api.get('/cerebro/aprendizaje/patrones-plazo')
      ]);
      setStats(statsRes.data);
      setConfig(configRes.data);
      setFeedbacks(feedbackRes.data.feedbacks || []);
      setSugerencias(sugerenciasRes.data.sugerencias || []);
      setPatronesPrecios(preciosRes.data);
      setPatronesPlazos(plazosRes.data);
      
      // Fases 6-9: Cargar datos avanzados
      try {
        const [resumenRes, relevanciaRes, reasignacionRes, tiempoRes, funcionariosRes] = await Promise.all([
          api.get('/cerebro/aprendizaje/resumen-avanzado'),
          api.get('/cerebro/aprendizaje/patrones-relevancia-tareas'),
          api.get('/cerebro/aprendizaje/patrones-reasignacion-area'),
          api.get('/cerebro/aprendizaje/metricas-tiempo-ejecucion'),
          api.get('/cerebro/aprendizaje/especializacion-funcionarios')
        ]);
        setResumenAvanzado(resumenRes.data);
        setPatronesRelevancia(relevanciaRes.data);
        setPatronesReasignacion(reasignacionRes.data);
        setMetricasTiempo(tiempoRes.data);
        setEspecializacionFuncionarios(funcionariosRes.data);
      } catch (advError) {
        console.log('Datos avanzados no disponibles:', advError);
      }
      
      // Fases 10-13: Motor de recomendación
      try {
        const motorRes = await api.get('/cerebro/recomendacion/estado');
        setMotorEstado(motorRes.data);
      } catch (motorError) {
        console.log('Motor de recomendación no disponible:', motorError);
      }

      // Fase 10 PSP: patrones aprendidos + feedback del Líder Comercial
      try {
        const [pspRes, lcRes] = await Promise.all([
          api.get('/cerebro/psp/patrones'),
          api.get('/cerebro/psp/feedback-lider-comercial?limit=20').catch(() => ({ data: { feedbacks: [] } })),
        ]);
        setPspPatrones(pspRes.data);
        setPspLiderFb(lcRes.data);
      } catch (pspErr) {
        console.log('Patrones PSP no disponibles:', pspErr);
      }
      
      // SHADOW: Cargar estadísticas y lista
      try {
        const [shadowStatsRes, shadowListaRes] = await Promise.all([
          api.get('/cerebro/shadow/estadisticas'),
          api.get('/cerebro/shadow/lista?limit=10')
        ]);
        setShadowStats(shadowStatsRes.data);
        setShadowLista(shadowListaRes.data.shadows || []);
      } catch (shadowError) {
        console.log('Datos SHADOW no disponibles:', shadowError);
      }
    } catch (error) {
      console.error('Error fetching learning data:', error);
      toast.error('Error al cargar datos de aprendizaje');
    } finally {
      setLoading(false);
    }
  };
  
  // Función para probar el motor de recomendación
  const testMotorRecomendacion = async () => {
    if (!testTarea.titulo) {
      toast.error('Ingresa un título de tarea para probar');
      return;
    }
    
    setTestLoading(true);
    setTestResultado(null);
    try {
      const res = await api.post('/cerebro/recomendacion/calibrar-tarea', testTarea);
      setTestResultado(res.data);
      toast.success('Calibración completada');
    } catch (error) {
      console.error('Error probando motor:', error);
      toast.error('Error al calibrar tarea');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/cerebro/aprendizaje/config', editConfig);
      setConfig(editConfig);
      setShowConfigModal(false);
      toast.success('Configuración guardada');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoObjetoLabel = (tipo) => {
    const labels = {
      'precio': 'Precio',
      'plazo': 'Plazo',
      'tarea': 'Tarea',
      'area': 'Área',
      'funcionario': 'Funcionario',
      'prioridad': 'Prioridad',
      'descripcion': 'Descripción'
    };
    return labels[tipo] || tipo;
  };

  const getTipoAccionBadge = (tipo) => {
    const config = {
      'aceptado': { label: 'Aceptado', color: 'bg-green-100 text-green-700' },
      'modificado': { label: 'Modificado', color: 'bg-yellow-100 text-yellow-700' },
      'eliminado': { label: 'Eliminado', color: 'bg-red-100 text-red-700' },
      'agregado': { label: 'Agregado', color: 'bg-blue-100 text-blue-700' }
    };
    const cfg = config[tipo] || { label: tipo, color: 'bg-[#0B132B] text-white/80' };
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-white/70">Cargando datos de aprendizaje...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brain-learning-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Aprendizaje del Cerebro</h2>
            <p className="text-sm text-white/70">Análisis de correcciones y mejora continua</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="default" 
            size="sm" 
            onClick={fetchData}
            className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
            data-testid="refresh-learning-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Actualizar
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="border-white/30 text-white hover:bg-white/10"
            onClick={() => {
              setEditConfig({...config});
              setShowConfigModal(true);
            }}
            data-testid="config-learning-btn"
          >
            <Settings className="h-4 w-4 mr-1" />
            Configuración
          </Button>
        </div>
      </div>

      {/* KPIs Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-indigo-900/40 to-[#162042] border-indigo-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-indigo-400 uppercase">Sugerencias</p>
              <p className="text-2xl font-bold text-white">{stats?.total_sugerencias || 0}</p>
            </div>
            <Brain className="h-8 w-8 text-indigo-400" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-900/40 to-[#162042] border-green-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-green-400 uppercase">Tasa Aceptación</p>
              <p className="text-2xl font-bold text-white">{stats?.tasa_aceptacion || 0}%</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-400" />
          </div>
          <Progress value={stats?.tasa_aceptacion || 0} className="mt-2 h-1.5" />
        </Card>

        <Card className="p-4 bg-gradient-to-br from-yellow-900/40 to-[#162042] border-yellow-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-yellow-400 uppercase">Modificaciones</p>
              <p className="text-2xl font-bold text-white">{stats?.total_modificadas || 0}</p>
            </div>
            <Edit2 className="h-8 w-8 text-yellow-400" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-purple-900/40 to-[#162042] border-purple-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-purple-400 uppercase">Total Feedback</p>
              <p className="text-2xl font-bold text-white">{stats?.total_feedback || 0}</p>
            </div>
            <BarChart3 className="h-8 w-8 text-purple-400" />
          </div>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-7 bg-[#0B132B] border border-[#3A86FF]/20">
          <TabsTrigger value="resumen" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white">
            <BarChart3 className="h-4 w-4" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="feedback" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white">
            <ListChecks className="h-4 w-4" />
            Feedback
          </TabsTrigger>
          <TabsTrigger value="patrones" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white">
            <Target className="h-4 w-4" />
            Patrones
          </TabsTrigger>
          <TabsTrigger value="psp" data-testid="tab-psp" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white">
            <Zap className="h-4 w-4" />
            PSP
          </TabsTrigger>
          <TabsTrigger value="avanzado" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white" data-testid="tab-avanzado">
            <Brain className="h-4 w-4" />
            Avanzado
          </TabsTrigger>
          <TabsTrigger value="motor" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white" data-testid="tab-motor">
            <Zap className="h-4 w-4" />
            Motor IA
          </TabsTrigger>
          <TabsTrigger value="usuarios" className="flex items-center gap-1.5 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white">
            <Users className="h-4 w-4" />
            Usuarios
          </TabsTrigger>
        </TabsList>

        {/* Tab: Resumen */}
        <TabsContent value="resumen" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Estadísticas por tipo */}
            <Card className="p-4 bg-[#162042] border-[#3A86FF]/20">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[#3A86FF]" />
                Categorías de Modificación
              </h3>
              <div className="space-y-3">
                {Object.entries(stats?.stats_por_tipo || {}).map(([tipo, data]) => (
                  <div key={tipo} className="flex items-center justify-between">
                    <span className="text-sm text-white/80 capitalize">{getTipoObjetoLabel(tipo)}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-[#0B132B] rounded-full h-2">
                        <div 
                          className="bg-[#3A86FF] h-2 rounded-full" 
                          style={{ width: `${Math.min((data.total / (stats?.total_feedback || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8 text-right text-white">{data.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Diferencias promedio */}
            <Card className="p-4 bg-[#162042] border-[#3A86FF]/20">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#3A86FF]" />
                Tendencias de Calibración
              </h3>
              <div className="space-y-4">
                <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-amber-400" />
                      <span className="text-sm text-white">Precios</span>
                    </div>
                    {stats?.diferencia_promedio_precio !== null ? (
                      <span className={`font-medium ${stats.diferencia_promedio_precio > 0 ? 'text-[#00E676]' : 'text-[#FF5252]'}`}>
                        {stats.diferencia_promedio_precio > 0 ? '+' : ''}{stats.diferencia_promedio_precio} UF
                      </span>
                    ) : (
                      <span className="text-white/50 text-sm">Sin datos</span>
                    )}
                  </div>
                  <p className="text-xs text-white/60 mt-1">
                    Diferencia entre precio sugerido y precio final aprobado
                  </p>
                </div>

                <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-400" />
                      <span className="text-sm text-white">Plazos</span>
                    </div>
                    {stats?.diferencia_promedio_plazo !== null ? (
                      <span className={`font-medium ${stats.diferencia_promedio_plazo > 0 ? 'text-amber-400' : 'text-[#00E676]'}`}>
                        {stats.diferencia_promedio_plazo > 0 ? '+' : ''}{stats.diferencia_promedio_plazo} días
                      </span>
                    ) : (
                      <span className="text-white/50 text-sm">Sin datos</span>
                    )}
                  </div>
                  <p className="text-xs text-white/60 mt-1">
                    Diferencia entre plazo sugerido y plazo final asignado
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Desglose de acciones */}
          <Card className="p-4 bg-[#162042] border-[#3A86FF]/20">
            <h3 className="font-semibold text-white mb-4">Acciones Rápidas</h3>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="p-3 bg-[#00E676]/10 rounded-lg border border-[#00E676]/20">
                <CheckCircle2 className="h-6 w-6 text-[#00E676] mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{stats?.total_aceptadas || 0}</p>
                <p className="text-xs text-white/70">Aceptadas</p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <Edit2 className="h-6 w-6 text-amber-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{stats?.total_modificadas || 0}</p>
                <p className="text-xs text-white/70">Modificadas</p>
              </div>
              <div className="p-3 bg-[#FF5252]/10 rounded-lg border border-[#FF5252]/20">
                <Trash2 className="h-6 w-6 text-[#FF5252] mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{stats?.total_eliminadas || 0}</p>
                <p className="text-xs text-white/70">Eliminadas</p>
              </div>
              <div className="p-3 bg-[#3A86FF]/10 rounded-lg border border-[#3A86FF]/20">
                <Plus className="h-6 w-6 text-[#3A86FF] mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{stats?.total_agregadas || 0}</p>
                <p className="text-xs text-white/70">Agregadas</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Feedback */}
        <TabsContent value="feedback" className="space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-indigo-600" />
              Historial de Correcciones
            </h3>
            {feedbacks.length === 0 ? (
              <div className="text-center py-8 text-white/60">
                <Brain className="h-12 w-12 mx-auto text-white/40 mb-2" />
                <p>No hay correcciones registradas aún</p>
                <p className="text-sm">El sistema comenzará a aprender cuando se procesen minutas y se realicen correcciones</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedbacks.map((fb) => (
                  <div key={fb.id} className="p-3 border rounded-lg hover:bg-[#162042] transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {getTipoObjetoLabel(fb.tipo_objeto)}
                          </Badge>
                          {getTipoAccionBadge(fb.tipo_accion)}
                          {fb.peso_usuario > 1 && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              Peso x{fb.peso_usuario}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-white/70 flex items-center gap-2">
                          {fb.valor_sugerido && (
                            <span className="line-through text-white/50">
                              {typeof fb.valor_sugerido === 'object' ? JSON.stringify(fb.valor_sugerido).slice(0, 50) : fb.valor_sugerido}
                            </span>
                          )}
                          {fb.valor_sugerido && fb.valor_final && (
                            <ArrowRight className="h-3 w-3 text-white/50" />
                          )}
                          {fb.valor_final && (
                            <span className="font-medium text-white">
                              {typeof fb.valor_final === 'object' ? JSON.stringify(fb.valor_final).slice(0, 50) : fb.valor_final}
                            </span>
                          )}
                        </div>
                        {fb.diferencia !== null && fb.diferencia !== undefined && (
                          <p className="text-xs text-white/60 mt-1">
                            Diferencia: {fb.diferencia > 0 ? '+' : ''}{fb.diferencia} 
                            {fb.diferencia_porcentaje !== null && ` (${fb.diferencia_porcentaje.toFixed(1)}%)`}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-white/50">
                        <p>{fb.usuario_nombre}</p>
                        <p>{formatDate(fb.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Tab: Patrones */}
        <TabsContent value="patrones" className="space-y-4">
          {/* Patrones de Precios y Plazos (Fase 4 y 5) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Patrón de Precios */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-amber-600" />
                Patrón de Precios Detectado
              </h3>
              {patronesPrecios?.patron_detectado ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <span className="text-sm text-white/80">Tendencia</span>
                    <Badge className={`${patronesPrecios.tendencia === 'subir' ? 'bg-green-100 text-green-700' : patronesPrecios.tendencia === 'bajar' ? 'bg-red-100 text-red-700' : 'bg-[#0B132B] text-white/80'}`}>
                      {patronesPrecios.tendencia === 'subir' ? '↑ Subir' : patronesPrecios.tendencia === 'bajar' ? '↓ Bajar' : '→ Mantener'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-[#162042] rounded text-center">
                      <p className="text-xs text-white/60">Correcciones</p>
                      <p className="font-bold text-white">{patronesPrecios.total_correcciones}</p>
                    </div>
                    <div className="p-2 bg-[#162042] rounded text-center">
                      <p className="text-xs text-white/60">Confianza</p>
                      <p className="font-bold text-white">{(patronesPrecios.confianza * 100).toFixed(0)}%</p>
                    </div>
                  </div>
                  <div className="p-3 bg-white border rounded-lg">
                    <p className="text-sm font-medium text-white mb-1">Ajuste Promedio</p>
                    <p className={`text-lg font-bold ${patronesPrecios.diferencia_promedio_ponderada > 0 ? 'text-green-600' : patronesPrecios.diferencia_promedio_ponderada < 0 ? 'text-red-600' : 'text-white/70'}`}>
                      {patronesPrecios.diferencia_promedio_ponderada > 0 ? '+' : ''}{patronesPrecios.diferencia_promedio_ponderada?.toFixed(1)}%
                    </p>
                    <p className="text-xs text-white/60 mt-1">{patronesPrecios.recomendacion}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-white/60">
                  <DollarSign className="h-10 w-10 mx-auto text-white/40 mb-2" />
                  <p className="text-sm">Sin suficientes datos</p>
                  <p className="text-xs text-white/50">Se necesitan más correcciones de precios para detectar patrones</p>
                </div>
              )}
            </Card>

            {/* Patrón de Plazos */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600" />
                Patrón de Plazos Detectado
              </h3>
              {patronesPlazos?.patron_detectado ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-white/80">Tendencia</span>
                    <Badge className={`${patronesPlazos.tendencia === 'extender' ? 'bg-amber-100 text-amber-700' : patronesPlazos.tendencia === 'acortar' ? 'bg-green-100 text-green-700' : 'bg-[#0B132B] text-white/80'}`}>
                      {patronesPlazos.tendencia === 'extender' ? '↑ Extender' : patronesPlazos.tendencia === 'acortar' ? '↓ Acortar' : '→ Mantener'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-[#162042] rounded text-center">
                      <p className="text-xs text-white/60">Correcciones</p>
                      <p className="font-bold text-white">{patronesPlazos.total_correcciones}</p>
                    </div>
                    <div className="p-2 bg-[#162042] rounded text-center">
                      <p className="text-xs text-white/60">Confianza</p>
                      <p className="font-bold text-white">{(patronesPlazos.confianza * 100).toFixed(0)}%</p>
                    </div>
                  </div>
                  <div className="p-3 bg-white border rounded-lg">
                    <p className="text-sm font-medium text-white mb-1">Ajuste Promedio</p>
                    <p className={`text-lg font-bold ${patronesPlazos.diferencia_promedio_ponderada > 0 ? 'text-amber-600' : patronesPlazos.diferencia_promedio_ponderada < 0 ? 'text-green-600' : 'text-white/70'}`}>
                      {patronesPlazos.diferencia_promedio_ponderada > 0 ? '+' : ''}{patronesPlazos.diferencia_promedio_ponderada?.toFixed(1)} días
                    </p>
                    <p className="text-xs text-white/60 mt-1">{patronesPlazos.recomendacion}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-white/60">
                  <Clock className="h-10 w-10 mx-auto text-white/40 mb-2" />
                  <p className="text-sm">Sin suficientes datos</p>
                  <p className="text-xs text-white/50">Se necesitan más correcciones de plazos para detectar patrones</p>
                </div>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reasignaciones de área más frecuentes */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-indigo-600" />
                Reasignaciones de Área Frecuentes
              </h3>
              {(stats?.areas_reasignadas_frecuentes || []).length === 0 ? (
                <p className="text-sm text-white/60 text-center py-4">Sin patrones detectados</p>
              ) : (
                <div className="space-y-2">
                  {stats.areas_reasignadas_frecuentes.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-[#162042] rounded">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{item.de}</Badge>
                        <ArrowRight className="h-3 w-3 text-white/50" />
                        <Badge className="bg-indigo-100 text-indigo-700 text-xs">{item.a}</Badge>
                      </div>
                      <span className="text-sm font-medium">{item.count}x</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Tareas eliminadas frecuentemente */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-red-600" />
                Tareas Eliminadas Frecuentemente
              </h3>
              {(stats?.tareas_eliminadas_frecuentes || []).length === 0 ? (
                <p className="text-sm text-white/60 text-center py-4">Sin patrones detectados</p>
              ) : (
                <div className="space-y-2">
                  {stats.tareas_eliminadas_frecuentes.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded">
                      <span className="text-sm text-white/80 truncate flex-1">{item.titulo}</span>
                      <Badge className="bg-red-100 text-red-700 text-xs ml-2">{item.count}x</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Tareas agregadas frecuentemente */}
            <Card className="p-4 md:col-span-2">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Plus className="h-4 w-4 text-green-600" />
                Tareas Agregadas Manualmente con Frecuencia
              </h3>
              {(stats?.tareas_agregadas_frecuentes || []).length === 0 ? (
                <p className="text-sm text-white/60 text-center py-4">
                  Cuando los usuarios agreguen tareas manualmente de forma repetida, aparecerán aquí como candidatas para sugerencias automáticas
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {stats.tareas_agregadas_frecuentes.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <div className="flex-1">
                        <span className="text-sm text-white/80">{item.titulo}</span>
                        {item.area && (
                          <Badge variant="outline" className="ml-2 text-xs">{item.area}</Badge>
                        )}
                      </div>
                      <Badge className="bg-green-100 text-green-700 text-xs">{item.count}x</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Usuarios */}
        <TabsContent value="usuarios" className="space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-indigo-600" />
              Usuarios con Peso Especial en Aprendizaje
            </h3>
            <p className="text-sm text-white/60 mb-4">
              Las correcciones de estos usuarios tienen mayor peso en el aprendizaje del Cerebro
            </p>
            <div className="space-y-2">
              {(config?.usuarios_peso_alto || []).map((user, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{user.nombre}</p>
                      <p className="text-sm text-white/60">{user.email}</p>
                    </div>
                  </div>
                  <Badge className="bg-purple-100 text-purple-700">
                    Peso x{user.peso}
                  </Badge>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-[#162042] rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/70">Peso por defecto para otros usuarios:</span>
                <Badge variant="outline">x{config?.peso_default || 1.0}</Badge>
              </div>
            </div>
          </Card>

          {/* Estado del aprendizaje */}
          <Card className="p-4">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Settings className="h-4 w-4 text-indigo-600" />
              Estado del Sistema de Aprendizaje
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config?.aprendizaje_activo ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Aprendizaje Activo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config?.aprender_precios ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Aprender Precios</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config?.aprender_plazos ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Aprender Plazos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config?.aprender_tareas ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Aprender Tareas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config?.aprender_areas ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Aprender Áreas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config?.aprender_funcionarios ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Aprender Funcionarios</span>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm text-white/60">
              <span>Min. ejemplos para aprender: <strong>{config?.min_ejemplos_para_aprender || 3}</strong></span>
              <span>Confianza mínima: <strong>{((config?.confianza_minima || 0.6) * 100).toFixed(0)}%</strong></span>
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Avanzado - Fases 6-9 */}
        <TabsContent value="avanzado" className="space-y-4" data-testid="tab-content-avanzado">
          {/* Resumen de Fases 6-9 */}
          <Card className="p-4">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Brain className="h-4 w-4 text-purple-600" />
              Estado del Aprendizaje Avanzado (Fases 6-9)
            </h3>
            {resumenAvanzado ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Fase 6 */}
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium">Relevancia Tareas</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-700">
                    {resumenAvanzado.fase_6_relevancia_tareas?.total_patrones || 0}
                  </div>
                  <p className="text-xs text-white/60 mt-1">
                    {resumenAvanzado.fase_6_relevancia_tareas?.tareas_marcadas_irrelevantes || 0} irrelevantes detectadas
                  </p>
                  <Badge className={`mt-2 ${resumenAvanzado.fase_6_relevancia_tareas?.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-[#0B132B] text-white/70'}`}>
                    {resumenAvanzado.fase_6_relevancia_tareas?.estado || 'sin datos'}
                  </Badge>
                </div>

                {/* Fase 7 */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRight className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">Reasignación Áreas</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    {resumenAvanzado.fase_7_reasignacion_areas?.total_patrones || 0}
                  </div>
                  <p className="text-xs text-white/60 mt-1">
                    {resumenAvanzado.fase_7_reasignacion_areas?.patrones_confiables || 0} patrones confiables
                  </p>
                  <Badge className={`mt-2 ${resumenAvanzado.fase_7_reasignacion_areas?.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {resumenAvanzado.fase_7_reasignacion_areas?.estado || 'aprendiendo'}
                  </Badge>
                </div>

                {/* Fase 8 */}
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium">Tiempos Ejecución</span>
                  </div>
                  <div className="text-2xl font-bold text-amber-700">
                    {resumenAvanzado.fase_8_tiempos_ejecucion?.total_tareas_tracked || 0}
                  </div>
                  <p className="text-xs text-white/60 mt-1">
                    {resumenAvanzado.fase_8_tiempos_ejecucion?.metricas_completas || 0} con métricas completas
                  </p>
                  <Badge className={`mt-2 ${resumenAvanzado.fase_8_tiempos_ejecucion?.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {resumenAvanzado.fase_8_tiempos_ejecucion?.estado || 'recopilando'}
                  </Badge>
                </div>

                {/* Fase 9 */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">Especialización</span>
                  </div>
                  <div className="text-2xl font-bold text-green-700">
                    {resumenAvanzado.fase_9_especializacion?.total_funcionarios || 0}
                  </div>
                  <p className="text-xs text-white/60 mt-1">
                    {resumenAvanzado.fase_9_especializacion?.funcionarios_con_datos_suficientes || 0} con datos suficientes
                  </p>
                  <Badge className={`mt-2 ${resumenAvanzado.fase_9_especializacion?.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {resumenAvanzado.fase_9_especializacion?.estado || 'aprendiendo'}
                  </Badge>
                </div>
              </div>
            ) : (
              <p className="text-white/60 text-sm">Cargando datos avanzados...</p>
            )}
            {resumenAvanzado && (
              <div className="mt-4 p-3 bg-[#162042] rounded-lg flex items-center justify-between">
                <span className="text-sm text-white/70">Fases activas:</span>
                <Badge className="bg-indigo-100 text-indigo-700">
                  {resumenAvanzado.resumen?.fases_activas || 0} / {resumenAvanzado.resumen?.total_fases || 4}
                </Badge>
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fase 6: Tareas Irrelevantes */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-red-600" />
                Tareas Frecuentemente Eliminadas
              </h3>
              {patronesRelevancia?.patrones_irrelevantes?.length > 0 ? (
                <div className="space-y-2">
                  {patronesRelevancia.patrones_irrelevantes.slice(0, 5).map((patron, idx) => (
                    <div key={idx} className="p-2 bg-red-50 rounded border border-red-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800">{patron.titulo}</span>
                        <Badge variant="outline" className="text-red-600">
                          {patron.veces_eliminada}x eliminada
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-white/60">
                        <span>Área: {patron.area}</span>
                        <span>•</span>
                        <span>Confianza: {((patron.confianza || 0) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-white/60">Aún no hay patrones de tareas irrelevantes detectados</p>
              )}
            </Card>

            {/* Fase 6: Tareas Manuales Recurrentes */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Plus className="h-4 w-4 text-green-600" />
                Tareas Manuales Sugeribles
              </h3>
              {patronesRelevancia?.patrones_manuales_sugeribles?.length > 0 ? (
                <div className="space-y-2">
                  {patronesRelevancia.patrones_manuales_sugeribles.slice(0, 5).map((patron, idx) => (
                    <div key={idx} className="p-2 bg-green-50 rounded border border-green-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800">{patron.titulo}</span>
                        <Badge variant="outline" className="text-green-600">
                          {patron.veces_agregada}x agregada
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-white/60">
                        <span>Área: {patron.area}</span>
                        <span>•</span>
                        <span>Confianza sugerencia: {((patron.confianza || 0) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-white/60">Aún no hay tareas manuales recurrentes para sugerir</p>
              )}
            </Card>

            {/* Fase 7: Reasignaciones de Área */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-blue-600" />
                Patrones de Reasignación de Área
              </h3>
              {patronesReasignacion?.recomendaciones?.length > 0 ? (
                <div className="space-y-2">
                  {patronesReasignacion.recomendaciones.slice(0, 5).map((patron, idx) => (
                    <div key={idx} className="p-2 bg-blue-50 rounded border border-blue-100">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-gray-800">"{patron.concepto}"</span>
                        <span className="text-white/50">•</span>
                        <span className="text-red-500">{patron.de}</span>
                        <ArrowRight className="h-3 w-3 text-white/50" />
                        <span className="text-green-600">{patron.a}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-white/60">
                        <span>{patron.frecuencia}x corregido</span>
                        <span>•</span>
                        <span>Confianza: {((patron.confianza || 0) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-white/60">Aún no hay patrones de reasignación de área detectados</p>
              )}
              {patronesReasignacion?.patron_mas_frecuente && (
                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                  <p className="text-xs text-amber-700 font-medium">Patrón más frecuente:</p>
                  <p className="text-sm text-white/80">
                    Cuando detecte "{patronesReasignacion.patron_mas_frecuente.concepto}", 
                    usar área <strong>{patronesReasignacion.patron_mas_frecuente.a}</strong> en lugar de {patronesReasignacion.patron_mas_frecuente.de}
                  </p>
                </div>
              )}
            </Card>

            {/* Fase 8: Métricas de Tiempo */}
            <Card className="p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                Métricas de Tiempo de Ejecución
              </h3>
              {metricasTiempo?.total_tareas_medidas > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 bg-[#162042] rounded text-center">
                      <div className="text-xl font-bold text-gray-800">
                        {metricasTiempo.tiempo_promedio_total_horas?.toFixed(1) || '-'}h
                      </div>
                      <p className="text-xs text-white/60">Tiempo promedio total</p>
                    </div>
                    <div className="p-2 bg-[#162042] rounded text-center">
                      <div className="text-xl font-bold text-gray-800">
                        {metricasTiempo.tasa_cumplimiento_plazo?.toFixed(1) || '-'}%
                      </div>
                      <p className="text-xs text-white/60">Cumplimiento de plazo</p>
                    </div>
                  </div>
                  {Object.entries(metricasTiempo.metricas_por_area || {}).length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-white/70">Por área:</p>
                      {Object.entries(metricasTiempo.metricas_por_area).slice(0, 4).map(([area, data]) => (
                        <div key={area} className="flex items-center justify-between text-xs p-1 bg-amber-50 rounded">
                          <span className="text-white/80">{area}</span>
                          <span className="text-amber-700">{data.tiempo_promedio_dias?.toFixed(1)} días prom.</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-white/60">Aún no hay suficientes datos de tiempos de ejecución</p>
              )}
            </Card>

            {/* Fase 9: Especialización de Funcionarios */}
            <Card className="p-4 md:col-span-2">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-green-600" />
                Especialización de Funcionarios
              </h3>
              {especializacionFuncionarios?.ranking_completo?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-white/60">
                        <th className="pb-2 font-medium">Funcionario</th>
                        <th className="pb-2 font-medium">Área</th>
                        <th className="pb-2 font-medium text-center">Tareas</th>
                        <th className="pb-2 font-medium text-center">Aprobación 1ra</th>
                        <th className="pb-2 font-medium text-center">Cumplimiento</th>
                        <th className="pb-2 font-medium text-center">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {especializacionFuncionarios.ranking_completo.slice(0, 10).map((func, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="py-2">
                            <div className="flex items-center gap-2">
                              {idx === 0 && <Badge className="bg-amber-100 text-amber-700">Top</Badge>}
                              <span className="font-medium">{func.nombre}</span>
                            </div>
                          </td>
                          <td className="py-2 text-white/70">{func.area_principal || '-'}</td>
                          <td className="py-2 text-center">{func.tareas_completadas}</td>
                          <td className="py-2 text-center">
                            <Badge variant="outline" className={func.tasa_aprobacion_primera >= 80 ? 'text-green-600' : 'text-amber-600'}>
                              {func.tasa_aprobacion_primera?.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="py-2 text-center">
                            <Badge variant="outline" className={func.tasa_cumplimiento_plazo >= 80 ? 'text-green-600' : 'text-amber-600'}>
                              {func.tasa_cumplimiento_plazo?.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div className="w-16 bg-[#0B132B] rounded-full h-2">
                                <div 
                                  className="bg-green-500 h-2 rounded-full" 
                                  style={{ width: `${(func.score || 0) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium">{((func.score || 0) * 100).toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-white/60">Aún no hay suficientes datos de especialización de funcionarios</p>
              )}
              {especializacionFuncionarios?.score_promedio_equipo && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-white/70">Score promedio del equipo:</span>
                  <Badge className="bg-green-100 text-green-700">
                    {((especializacionFuncionarios.score_promedio_equipo || 0) * 100).toFixed(0)}%
                  </Badge>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Motor IA - Fases 10-13 */}
        <TabsContent value="motor" className="space-y-4" data-testid="tab-content-motor">
          {/* Estado del Motor */}
          <Card className="p-4">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-600" />
              Motor de Recomendación Inteligente
            </h3>
            {motorEstado ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-800">Versión: {motorEstado.motor_recomendacion?.version}</span>
                    <span className="text-sm text-white/60 ml-3">Fases {motorEstado.motor_recomendacion?.fases}</span>
                  </div>
                  <Badge className={
                    motorEstado.motor_recomendacion?.estado_general === 'operativo' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-amber-100 text-amber-700'
                  }>
                    {motorEstado.motor_recomendacion?.estado_general}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(motorEstado.capacidades || {}).map(([key, cap]) => (
                    <div key={key} className="p-3 bg-white border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        {key === 'precio' && <DollarSign className="h-4 w-4 text-green-600" />}
                        {key === 'plazo' && <Clock className="h-4 w-4 text-blue-600" />}
                        {key === 'area' && <Target className="h-4 w-4 text-purple-600" />}
                        {key === 'funcionario' && <UserCheck className="h-4 w-4 text-indigo-600" />}
                        {key === 'relevancia' && <ListChecks className="h-4 w-4 text-amber-600" />}
                        <span className="text-sm font-medium capitalize">{key}</span>
                      </div>
                      <div className="text-xl font-bold text-gray-800">{cap.datos_disponibles}</div>
                      <Badge variant="outline" className={`mt-1 text-xs ${
                        cap.estado === 'maduro' ? 'text-green-600 border-green-300' :
                        cap.estado === 'operativo' ? 'text-blue-600 border-blue-300' :
                        cap.estado === 'aprendiendo' ? 'text-amber-600 border-amber-300' :
                        'text-white/60 border-gray-300'
                      }`}>
                        {cap.estado}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-white/60 text-sm">Cargando estado del motor...</p>
            )}
          </Card>

          {/* Probador del Motor */}
          <Card className="p-4">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <PlayCircle className="h-4 w-4 text-blue-600" />
              Probar Motor de Recomendación
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <Label>Título de la tarea</Label>
                  <Input 
                    placeholder="Ej: Revisión declaración TGR pendiente"
                    value={testTarea.titulo}
                    onChange={(e) => setTestTarea({...testTarea, titulo: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Descripción (opcional)</Label>
                  <Textarea 
                    placeholder="Descripción de la tarea..."
                    value={testTarea.descripcion}
                    onChange={(e) => setTestTarea({...testTarea, descripcion: e.target.value})}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Área</Label>
                    <Select value={testTarea.area} onValueChange={(v) => setTestTarea({...testTarea, area: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Legal">Legal</SelectItem>
                        <SelectItem value="Tributaria">Tributaria</SelectItem>
                        <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                        <SelectItem value="Administrativa">Administrativa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Plazo (días)</Label>
                    <Input 
                      type="number"
                      value={testTarea.plazo_dias}
                      onChange={(e) => setTestTarea({...testTarea, plazo_dias: parseInt(e.target.value) || 7})}
                    />
                  </div>
                  <div>
                    <Label>Precio (UF)</Label>
                    <Input 
                      type="number"
                      placeholder="Opcional"
                      value={testTarea.precio || ''}
                      onChange={(e) => setTestTarea({...testTarea, precio: e.target.value ? parseFloat(e.target.value) : null})}
                    />
                  </div>
                </div>
                <Button 
                  onClick={testMotorRecomendacion} 
                  disabled={testLoading || !testTarea.titulo}
                  className="w-full"
                >
                  {testLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calibrando...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Calibrar Tarea
                    </>
                  )}
                </Button>
              </div>
              
              {/* Resultados */}
              <div className="bg-[#162042] rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Resultado de Calibración
                </h4>
                {testResultado ? (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-white/70">Confianza Global:</span>
                      <Badge className={testResultado.confidence_global > 0.5 ? 'bg-green-100 text-green-700' : 'bg-[#0B132B] text-white/70'}>
                        {(testResultado.confidence_global * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    
                    {/* Área */}
                    {testResultado.recomendaciones?.area?.cambio_sugerido && (
                      <div className="p-2 bg-purple-50 rounded border border-purple-100">
                        <div className="flex items-center gap-1 text-purple-700">
                          <Target className="h-3 w-3" />
                          <span className="font-medium">Área</span>
                        </div>
                        <p className="text-xs text-white/70 mt-1">
                          {testResultado.recomendaciones.area.area_original_ia} → <strong>{testResultado.recomendaciones.area.area_recomendada}</strong>
                        </p>
                        <p className="text-xs text-white/60 mt-1">
                          {testResultado.recomendaciones.area.explicacion}
                        </p>
                      </div>
                    )}
                    
                    {/* Plazo */}
                    {testResultado.recomendaciones?.plazo?.diferencia_dias !== 0 && (
                      <div className="p-2 bg-blue-50 rounded border border-blue-100">
                        <div className="flex items-center gap-1 text-blue-700">
                          <Clock className="h-3 w-3" />
                          <span className="font-medium">Plazo</span>
                        </div>
                        <p className="text-xs text-white/70 mt-1">
                          {testResultado.recomendaciones.plazo.plazo_original_ia} días → <strong>{testResultado.recomendaciones.plazo.plazo_calibrado} días</strong>
                        </p>
                        <p className="text-xs text-white/60 mt-1">
                          {testResultado.recomendaciones.plazo.explicacion}
                        </p>
                      </div>
                    )}
                    
                    {/* Precio */}
                    {testResultado.recomendaciones?.precio?.ajuste_aplicado_pct !== 0 && (
                      <div className="p-2 bg-green-50 rounded border border-green-100">
                        <div className="flex items-center gap-1 text-green-700">
                          <DollarSign className="h-3 w-3" />
                          <span className="font-medium">Precio</span>
                        </div>
                        <p className="text-xs text-white/70 mt-1">
                          {testResultado.recomendaciones.precio.precio_original_ia} UF → <strong>{testResultado.recomendaciones.precio.precio_calibrado} UF</strong>
                          <span className="ml-1 text-white/50">({testResultado.recomendaciones.precio.ajuste_aplicado_pct > 0 ? '+' : ''}{testResultado.recomendaciones.precio.ajuste_aplicado_pct.toFixed(1)}%)</span>
                        </p>
                      </div>
                    )}
                    
                    {/* Funcionario */}
                    {testResultado.recomendaciones?.funcionario?.funcionario_recomendado && (
                      <div className="p-2 bg-indigo-50 rounded border border-indigo-100">
                        <div className="flex items-center gap-1 text-indigo-700">
                          <UserCheck className="h-3 w-3" />
                          <span className="font-medium">Funcionario Sugerido</span>
                        </div>
                        <p className="text-xs text-white/70 mt-1">
                          <strong>{testResultado.recomendaciones.funcionario.funcionario_recomendado.nombre}</strong>
                        </p>
                        <p className="text-xs text-white/60 mt-1">
                          Score: {(testResultado.recomendaciones.funcionario.funcionario_recomendado.score * 100).toFixed(0)}%
                        </p>
                      </div>
                    )}
                    
                    {/* Relevancia */}
                    {testResultado.recomendaciones?.relevancia?.relevancia === 'irrelevante' && (
                      <div className="p-2 bg-red-50 rounded border border-red-100">
                        <div className="flex items-center gap-1 text-red-700">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="font-medium">Advertencia de Relevancia</span>
                        </div>
                        <p className="text-xs text-white/70 mt-1">
                          {testResultado.recomendaciones.relevancia.explicacion}
                        </p>
                      </div>
                    )}
                    
                    {/* Sin cambios */}
                    {testResultado.resumen_explicacion === "Sin cambios significativos respecto a sugerencia base de IA." && (
                      <div className="p-2 bg-[#0B132B] rounded flex items-center gap-2 text-white/70">
                        <Info className="h-4 w-4" />
                        <span className="text-xs">Sin calibraciones disponibles. El motor aprenderá de las correcciones futuras.</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-white/60">Ingresa una tarea y haz clic en "Calibrar" para ver las recomendaciones del motor.</p>
                )}
              </div>
            </div>
          </Card>

          {/* Explicación del Motor */}
          <Card className="p-4 bg-gradient-to-br from-slate-50 to-gray-50">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-600" />
              ¿Cómo funciona el Motor de Recomendación?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/70">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Fuentes de Datos</h4>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Correcciones de precio históricas</li>
                  <li>Ajustes de plazo realizados por usuarios</li>
                  <li>Reasignaciones de área frecuentes</li>
                  <li>Tiempos reales de ejecución de tareas</li>
                  <li>Desempeño histórico de funcionarios</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Cálculo de Confianza</h4>
                <ul className="space-y-1 list-disc list-inside">
                  <li><strong>0%:</strong> Sin datos históricos</li>
                  <li><strong>1-50%:</strong> Aprendiendo (pocos ejemplos)</li>
                  <li><strong>50-80%:</strong> Operativo (ejemplos suficientes)</li>
                  <li><strong>80-100%:</strong> Maduro (muchos ejemplos consistentes)</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Importante:</strong> El motor NO realiza cambios automáticos. Solo sugiere calibraciones basadas en histórico.
                  La decisión final siempre es del usuario.
                </span>
              </p>
            </div>
          </Card>

          {/* Modo SHADOW - Comparación */}
          <Card className="p-4 border-2 border-dashed border-purple-200 bg-purple-50/30">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Eye className="h-4 w-4 text-purple-600" />
              Modo SHADOW (Observador)
              <Badge className="bg-purple-100 text-purple-700 ml-2">Automático</Badge>
            </h3>
            
            <p className="text-sm text-white/70 mb-4">
              El motor se ejecuta automáticamente en cada nueva minuta procesada, pero <strong>sin modificar</strong> las sugerencias visibles.
              Esto permite comparar la precisión del motor vs la IA base.
            </p>
            
            {shadowStats ? (
              <div className="space-y-4">
                {/* Estadísticas globales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-purple-700">{shadowStats.total_minutas_evaluadas || 0}</div>
                    <p className="text-xs text-white/60">Minutas evaluadas</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-purple-700">{shadowStats.total_tareas_evaluadas || 0}</div>
                    <p className="text-xs text-white/60">Tareas procesadas</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-purple-700">{shadowStats.precision_comparada?.total_comparaciones || 0}</div>
                    <p className="text-xs text-white/60">Comparaciones</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-purple-700">{((shadowStats.confidence_promedio_motor || 0) * 100).toFixed(0)}%</div>
                    <p className="text-xs text-white/60">Confianza promedio</p>
                  </div>
                </div>
                
                {/* Comparación de precisión: Motor vs IA */}
                {shadowStats.precision_comparada?.total_comparaciones > 0 && (
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <GitCompare className="h-4 w-4 text-indigo-600" />
                      Comparación: Motor Calibrado vs IA Base
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h5 className="text-sm font-medium text-white/70 mb-2">Aciertos en Área</h5>
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-purple-600">Motor</span>
                              <span className="font-medium">{shadowStats.precision_comparada.motor.pct_area || 0}%</span>
                            </div>
                            <div className="w-full bg-[#0B132B] rounded-full h-2">
                              <div 
                                className="bg-purple-500 h-2 rounded-full" 
                                style={{ width: `${shadowStats.precision_comparada.motor.pct_area || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-white/60">IA Base</span>
                              <span className="font-medium">{shadowStats.precision_comparada.ia_base.pct_area || 0}%</span>
                            </div>
                            <div className="w-full bg-[#0B132B] rounded-full h-2">
                              <div 
                                className="bg-gray-400 h-2 rounded-full" 
                                style={{ width: `${shadowStats.precision_comparada.ia_base.pct_area || 0}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-white/70 mb-2">Aciertos en Plazo (±2 días)</h5>
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-purple-600">Motor</span>
                              <span className="font-medium">{shadowStats.precision_comparada.motor.pct_plazo || 0}%</span>
                            </div>
                            <div className="w-full bg-[#0B132B] rounded-full h-2">
                              <div 
                                className="bg-purple-500 h-2 rounded-full" 
                                style={{ width: `${shadowStats.precision_comparada.motor.pct_plazo || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-white/60">IA Base</span>
                              <span className="font-medium">{shadowStats.precision_comparada.ia_base.pct_plazo || 0}%</span>
                            </div>
                            <div className="w-full bg-[#0B132B] rounded-full h-2">
                              <div 
                                className="bg-gray-400 h-2 rounded-full" 
                                style={{ width: `${shadowStats.precision_comparada.ia_base.pct_plazo || 0}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Cambios sugeridos por el motor */}
                {shadowStats.cambios_sugeridos && (
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-800 mb-3">Cambios Sugeridos por el Motor (SHADOW)</h4>
                    <div className="grid grid-cols-5 gap-2 text-sm">
                      <div className="text-center p-2 bg-purple-50 rounded">
                        <div className="font-bold text-purple-700">{shadowStats.cambios_sugeridos.area || 0}</div>
                        <p className="text-xs text-white/60">Área</p>
                      </div>
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <div className="font-bold text-blue-700">{shadowStats.cambios_sugeridos.plazo || 0}</div>
                        <p className="text-xs text-white/60">Plazo</p>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="font-bold text-green-700">{shadowStats.cambios_sugeridos.precio || 0}</div>
                        <p className="text-xs text-white/60">Precio</p>
                      </div>
                      <div className="text-center p-2 bg-indigo-50 rounded">
                        <div className="font-bold text-indigo-700">{shadowStats.cambios_sugeridos.funcionario || 0}</div>
                        <p className="text-xs text-white/60">Funcionario</p>
                      </div>
                      <div className="text-center p-2 bg-red-50 rounded">
                        <div className="font-bold text-red-700">{shadowStats.cambios_sugeridos.irrelevantes || 0}</div>
                        <p className="text-xs text-white/60">Irrelevantes</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {shadowStats.recomendacion && (
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <p className="text-xs text-purple-700">
                      <strong>Estado:</strong> {shadowStats.estado_modo_shadow === 'activo' ? 'El modo SHADOW está activo' : 'Inactivo'}. 
                      {' '}{shadowStats.recomendacion}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-6 text-white/60">
                <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">El modo SHADOW se activará automáticamente al procesar nuevas minutas.</p>
                <p className="text-xs mt-1">Aún no hay datos de comparación disponibles.</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Tab: PSP — Fase 10 (Aprendizaje de PSP completa) */}
        <TabsContent value="psp" className="space-y-4" data-testid="tab-content-psp">
          <Card className="p-4 bg-[#162042] border-[#3A86FF]/20">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold" data-testid="psp-title">
                  Patrones aprendidos de PSPs (sugerida vs. final)
                </h3>
                <p className="text-xs text-white/60">
                  Se activan con ≥3 PSPs distintas, minutas distintas, servicios similares
                  y modificaciones comparables. Regla:{' '}
                  <span className="font-mono text-indigo-300">
                    {pspPatrones?.patrones?.[0]?.similarity_rule_version || 'v1.0-strict-2026-02'}
                  </span>
                </p>
              </div>
              <Badge variant="outline" className="border-indigo-400 text-indigo-200">
                {pspPatrones?.total ?? 0} patrones
              </Badge>
            </div>

            {!pspPatrones || (pspPatrones.total ?? 0) === 0 ? (
              <div className="text-center py-8 text-white/50" data-testid="psp-empty">
                Sin patrones aún. Aprendizaje activo cuando existan ≥3 diferencias comparables entre
                sugerencia IA y PSP final.
              </div>
            ) : (
              <div className="space-y-2">
                {(pspPatrones.patrones || []).map((p) => (
                  <div
                    key={p.id}
                    className="border border-[#3A86FF]/20 rounded-md p-3 bg-[#0B132B]"
                    data-testid={`psp-pattern-${p.atributo}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-indigo-600/40 text-indigo-100 border-indigo-400">
                          {p.atributo}
                        </Badge>
                        {p.activo ? (
                          <Badge className="bg-green-600/40 text-green-100 border-green-400">
                            ACTIVO
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-white/50">
                            en aprendizaje
                          </Badge>
                        )}
                        <span className="text-xs text-white/60">
                          {p.n_casos} PSPs · {p.n_minutas_distintas || 0} minutas · conf.{' '}
                          {Math.round((p.confianza || 0) * 100)}%
                        </span>
                      </div>
                      <div className="text-xs text-white/40">
                        tendencia: <span className="text-white/70">{p.tendencia}</span>
                      </div>
                    </div>
                    <div className="text-xs text-white/70 space-y-1">
                      <div>
                        <span className="text-white/50">Original: </span>
                        <span className="font-mono text-amber-300">
                          {p.diferencias && p.diferencias.length > 0 && p.diferencia_promedio != null
                            ? (typeof p.valor_recomendado === 'number'
                                ? (p.valor_recomendado - p.diferencia_promedio).toFixed(2)
                                : '—')
                            : '—'}
                        </span>
                        <span className="mx-2 text-white/40">→</span>
                        <span className="text-white/50">Recomendado: </span>
                        <span className="font-mono text-emerald-300">
                          {typeof p.valor_recomendado === 'object'
                            ? JSON.stringify(p.valor_recomendado).slice(0, 80)
                            : String(p.valor_recomendado)}
                        </span>
                        {p.diferencia_promedio != null && (
                          <span className="ml-2 text-white/50">
                            (Δ prom: {p.diferencia_promedio})
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-white/50">PSPs referencia: </span>
                        <span className="font-mono text-white/60">
                          {(p.psp_ids_muestrales || []).slice(0, 3).join(', ')}
                          {(p.psp_ids_muestrales || []).length > 3 && ` +${p.psp_ids_muestrales.length - 3}`}
                        </span>
                      </div>
                      {p.task_key && (
                        <div>
                          <span className="text-white/50">Tarea: </span>
                          <span className="text-white/80">{p.task_key}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Modificaciones del Líder del Área Comercial (Fase 10) */}
          <Card className="p-4 bg-[#162042] border-[#3A86FF]/20" data-testid="psp-lider-comercial-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold">
                  Modificaciones del Líder del Área Comercial
                </h3>
                <p className="text-xs text-white/60">
                  Feedback registrado con rol, usuario, peso aplicado y fecha. El Líder Comercial
                  es un actor válido: sus correcciones contribuyen al aprendizaje.
                </p>
              </div>
              <Badge variant="outline" className="border-fuchsia-400 text-fuchsia-200">
                {pspLiderFb?.total ?? 0} feedbacks
              </Badge>
            </div>
            {!pspLiderFb || (pspLiderFb.total ?? 0) === 0 ? (
              <div className="text-center py-6 text-white/50" data-testid="lider-fb-empty">
                Aún no se han registrado modificaciones del Líder Comercial.
              </div>
            ) : (
              <div className="space-y-2">
                {(pspLiderFb.feedbacks || []).slice(0, 10).map((f) => (
                  <div
                    key={f.id}
                    className="border border-fuchsia-500/20 rounded-md p-3 bg-[#0B132B] text-xs"
                    data-testid={`lider-fb-${f.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className="bg-fuchsia-600/40 text-fuchsia-100 border-fuchsia-400">
                        {f.tipo_objeto}
                      </Badge>
                      <Badge variant="outline" className="text-white/70">{f.tipo_accion}</Badge>
                      <span className="text-white/70">{f.usuario_nombre}</span>
                      <span className="text-white/40">·</span>
                      <span className="text-white/50">rol: <span className="text-white/70">{f.usuario_rol}</span></span>
                      <span className="text-white/40">·</span>
                      <span className="text-white/50">peso: <span className="text-emerald-300">{f.peso_usuario}</span></span>
                      <span className="text-white/40">·</span>
                      <span className="text-white/50">{f.created_at ? new Date(f.created_at).toLocaleString('es-CL') : ''}</span>
                    </div>
                    <div className="text-white/70">
                      <span className="text-white/50">Sugerido: </span>
                      <span className="font-mono text-amber-300">
                        {typeof f.valor_sugerido === 'object'
                          ? JSON.stringify(f.valor_sugerido).slice(0, 60)
                          : String(f.valor_sugerido ?? '—')}
                      </span>
                      <span className="mx-2 text-white/40">→</span>
                      <span className="text-white/50">Final: </span>
                      <span className="font-mono text-emerald-300">
                        {typeof f.valor_final === 'object'
                          ? JSON.stringify(f.valor_final).slice(0, 60)
                          : String(f.valor_final ?? '—')}
                      </span>
                      {f.diferencia != null && (
                        <span className="ml-2 text-white/50">(Δ {f.diferencia})</span>
                      )}
                    </div>
                    <div className="text-white/40 mt-1">
                      PSP: <span className="font-mono">{f.proposal_id}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

        </TabsContent>

      </Tabs>

      {/* Modal de Configuración */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuración de Aprendizaje
            </DialogTitle>
            <DialogDescription>
              Ajusta los parámetros del sistema de aprendizaje del Cerebro
            </DialogDescription>
          </DialogHeader>
          
          {editConfig && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label>Aprendizaje Activo</Label>
                <Switch 
                  checked={editConfig.aprendizaje_activo}
                  onCheckedChange={(checked) => setEditConfig({...editConfig, aprendizaje_activo: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Registrar Feedback</Label>
                <Switch 
                  checked={editConfig.registrar_feedback}
                  onCheckedChange={(checked) => setEditConfig({...editConfig, registrar_feedback: checked})}
                />
              </div>

              <div className="border-t pt-4 space-y-3">
                <Label className="text-sm font-medium">Dominios de Aprendizaje</Label>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Precios</span>
                  <Switch 
                    checked={editConfig.aprender_precios}
                    onCheckedChange={(checked) => setEditConfig({...editConfig, aprender_precios: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Plazos</span>
                  <Switch 
                    checked={editConfig.aprender_plazos}
                    onCheckedChange={(checked) => setEditConfig({...editConfig, aprender_plazos: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Tareas</span>
                  <Switch 
                    checked={editConfig.aprender_tareas}
                    onCheckedChange={(checked) => setEditConfig({...editConfig, aprender_tareas: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Áreas</span>
                  <Switch 
                    checked={editConfig.aprender_areas}
                    onCheckedChange={(checked) => setEditConfig({...editConfig, aprender_areas: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm">Funcionarios</span>
                  <Switch 
                    checked={editConfig.aprender_funcionarios}
                    onCheckedChange={(checked) => setEditConfig({...editConfig, aprender_funcionarios: checked})}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div>
                  <Label className="text-sm">Mínimo de ejemplos para aprender</Label>
                  <Input 
                    type="number"
                    min={1}
                    max={20}
                    value={editConfig.min_ejemplos_para_aprender}
                    onChange={(e) => setEditConfig({...editConfig, min_ejemplos_para_aprender: parseInt(e.target.value) || 3})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Confianza mínima (0-1)</Label>
                  <Input 
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    value={editConfig.confianza_minima}
                    onChange={(e) => setEditConfig({...editConfig, confianza_minima: parseFloat(e.target.value) || 0.6})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig}>
              {savingConfig ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrainLearningPanel;
